import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Checkbox from '../../common/Form/Checkbox';
import Input from '../../common/Form/Input';
import Select from '../../common/Form/Select';
import TextArea from '../../common/Form/TextArea';
import config from '../../config';
import { debounce, getNewAuthToken, getOrgId } from '../../utils/common';
import { getPavAssetFieldType, getPavWorkingConditionLabel } from './pavAssetUtils';

const SELECT_TYPES = new Set(['SELECT', 'DROPDOWN', 'MULTI_SELECT']);

const normalizeOption = (option, index, cta) => {
    if (option === undefined || option === null) return null;
    if (typeof option !== 'object') {
        return { key: String(option), value: option, label: String(option) };
    }

    const valueKey = cta?.value_key || cta?.valueKey;
    const labelKey = cta?.label_key || cta?.labelKey;
    const value = (valueKey ? option[valueKey] : undefined) ?? option.value ?? option.id ?? option.key;
    const label = (labelKey ? option[labelKey] : undefined) ?? option.label ?? option.name ?? option.title ?? value;
    if (value === undefined || value === null) return null;
    return { ...option, key: option.key ?? option.id ?? `${value}-${index}`, value, label: String(label) };
};

const normalizeOptions = (options, cta) => (Array.isArray(options) ? options : [])
    .map((option, index) => normalizeOption(option, index, cta))
    .filter(Boolean);

// Keyed off the option's value, not its label, so only the `NEW` working
// condition is relabelled and the value the form submits stays untouched.
const applyDisplayLabel = (fieldKey, option) => {
    const displayLabel = getPavWorkingConditionLabel(fieldKey, option.value);
    return displayLabel === option.value ? option : { ...option, label: String(displayLabel) };
};

const getLoadCta = (field) => {
    const ctas = Array.isArray(field?.cta) ? field.cta : field?.cta ? [field.cta] : [];
    return ctas.find((cta) => String(cta?.call_on || 'LOAD').toUpperCase() === 'LOAD');
};

const buildOptionsUrl = (cta, search = '') => {
    if (!cta?.path) return '';
    let url;
    if (/^https?:\/\//i.test(cta.path)) {
        url = cta.path;
    } else {
        const baseUrl = cta.platform === 'ICEBERG' ? config.api.icebergApiUrl : config.api.scogoApiUrl;
        const modulePath = cta.module ? `/${String(cta.module).toLowerCase()}` : '';
        const path = String(cta.path).startsWith('/') ? cta.path : `/${cta.path}`;
        url = `${baseUrl}/${config.api.version}${modulePath}${path}`;
    }

    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', '100');
    if (search) params.set('q', search);
    return `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
};

const getResponseItems = (response) => {
    const payload = response?.data?.data ?? response?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

const getInputType = (fieldType) => {
    if (fieldType === 'NUMBER') return 'number';
    if (fieldType === 'EMAIL') return 'email';
    if (fieldType === 'DATE') return 'date';
    if (['PHONE', 'TEL', 'TELEPHONE'].includes(fieldType)) return 'tel';
    if (fieldType === 'URL') return 'url';
    return 'text';
};

export default function PavDynamicAssetField({ field, name = field?.key, readOnly = false, inputStyle, inputClassName, labelClassName, disableResponsiveFontSize = false, isPavMilestoneAsset = false }) {
    const fieldType = getPavAssetFieldType(field);
    const isSelect = SELECT_TYPES.has(fieldType);
    const isMulti = fieldType === 'MULTI_SELECT';
    const loadCta = useMemo(() => getLoadCta(field), [field]);
    const inlineOptions = useMemo(() => normalizeOptions(field?.options, loadCta), [field?.options, loadCta]);
    const [remoteOptions, setRemoteOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { control, setValue } = useFormContext();
    const currentValue = useWatch({ control, name });

    const fetchOptions = useCallback(async (search = '') => {
        const url = buildOptionsUrl(loadCta, search);
        if (!url) return;
        setIsLoading(true);
        try {
            const response = await axios.get(url, {
                headers: {
                    'x-org-id': getOrgId(),
                    authorization: `Bearer ${getNewAuthToken()}`,
                },
            });
            setRemoteOptions(normalizeOptions(getResponseItems(response), loadCta));
        } catch {
            setRemoteOptions([]);
        } finally {
            setIsLoading(false);
        }
    }, [loadCta]);

    const debouncedFetchOptions = useMemo(() => debounce(fetchOptions), [fetchOptions]);

    useEffect(() => {
        setRemoteOptions([]);
        if (isSelect && loadCta && inlineOptions.length === 0) fetchOptions();
    }, [isSelect, loadCta, inlineOptions.length, fetchOptions]);

    const options = useMemo(() => {
        const loadedOptions = remoteOptions.length > 0 ? remoteOptions : inlineOptions;
        // This component is also reused by the BOQ and add-ticket-asset forms, so
        // the working-condition relabel is opt-in and stays limited to PAV assets.
        if (!isPavMilestoneAsset) return loadedOptions;
        return loadedOptions.map((option) => applyDisplayLabel(field?.key, option));
    }, [remoteOptions, inlineOptions, field?.key, isPavMilestoneAsset]);

    useEffect(() => {
        if (!isMulti || !Array.isArray(currentValue) || options.length === 0) return;
        const normalizedValue = currentValue.map((value) => {
            if (value && typeof value === 'object') return value;
            return options.find((option) => String(option.value) === String(value));
        }).filter(Boolean);
        if (normalizedValue.length !== currentValue.length || normalizedValue.some((value, index) => value !== currentValue[index])) {
            setValue(name, normalizedValue, { shouldDirty: false, shouldValidate: true });
        }
    }, [currentValue, isMulti, name, options, setValue]);

    if (isSelect) {
        return (
            <Select
                name={name}
                label={field?.label || name}
                placeholder={`Select ${field?.label || name}`}
                required={Boolean(field?.is_required)}
                options={options}
                isMulti={isMulti}
                isLoading={isLoading}
                readOnly={readOnly}
                labelClassName={labelClassName}
                findOption={(availableOptions, value) => availableOptions.find((option) => String(option.value) === String(value))}
                onFocus={() => {
                    if (loadCta && remoteOptions.length === 0) fetchOptions();
                }}
                onInputChange={(search) => {
                    if (loadCta) debouncedFetchOptions(search);
                }}
            />
        );
    }

    if (['TEXTAREA', 'LONG_TEXT'].includes(fieldType)) {
        return <TextArea name={name} label={field?.label || name} required={Boolean(field?.is_required)} readOnly={readOnly} placeholder={`Enter ${field?.label || name}`} height='2.5rem' padding='px-2 pt-0' />;
    }

    if (['BOOLEAN', 'CHECKBOX'].includes(fieldType)) {
        return <Checkbox name={name} label={field?.label || name} required={Boolean(field?.is_required)} className='px-2 py-1 inline-flex items-center' />;
    }

    return (
        <Input
            label={field?.label || name}
            name={name}
            type={getInputType(fieldType)}
            required={Boolean(field?.is_required)}
            readOnly={readOnly}
            placeholder={`Enter ${field?.label || name}`}
            inputStyle={inputStyle}
            inputClassName={inputClassName}
            labelClassName={labelClassName}
            disableResponsiveFontSize={disableResponsiveFontSize}
        />
    );
}
